'use client';

// [DialogHost]
// this renders Dialogs from DialogManager in the tree to a specific scope
// for example, there is a "Game" scope in play.tsx that has access to
// GameContext, so adding Dialogs to this scope will give access to game
// information
//
// it also plays the "open popup" sound when rendering

import { DialogHostScope } from "@/src/enum/dialog";
import { useDialogManager } from "./dialog";
import { useEffect, useMemo } from "react";
import { useSoundManager } from "./sound/soundmanager";
import SoundId from "./sound/soundid";

type Props = {
    scope?: DialogHostScope;
}

export default function DialogHost(
    {
        scope,
    }: Props,
): JSX.Element | null {
    const { currentDialog, currentDialogEntry } = useDialogManager();
    const { playSound } = useSoundManager();

    const dialogToShow = useMemo(
        (): JSX.Element | null => {
            if (
                currentDialog != null
                && currentDialogEntry != null
                && currentDialogEntry.scope === scope
            ) {
                return currentDialog;
            }

            return null;
        },
        [scope, currentDialog, currentDialogEntry],
    );

    // play the open sound effect when a new popup is added
    // we look at the key property since there could be state changes
    // on the dialog entry itself and would trigger this
    useEffect(
        () => {
            if (dialogToShow?.key) {
                playSound(SoundId.Popup);
            }
        },
        [dialogToShow?.key, playSound],
    );

    return dialogToShow;
}
